import * as React from 'react';
import { Button } from '@/components/ui/button';
import { FaDownload, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function AppInstallPrompt() {
  const { t } = useTranslation();
  const [installPrompt, setInstallPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = React.useState(false);

  React.useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event so it can be triggered later
      setInstallPrompt(e);
      // Check if user has previously dismissed or installed
      const hasPromptBeenShown = localStorage.getItem('installPromptShown');
      if (!hasPromptBeenShown) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler as any);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler as any);
    };
  }, []);

  const handleInstallClick = () => {
    if (!installPrompt) return;

    // Show the install prompt
    installPrompt.prompt();

    // Wait for the user to respond to the prompt
    installPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      // Clear the saved prompt since it can't be used again
      setInstallPrompt(null);
      setShowPrompt(false);
      // Remember that we've shown the prompt
      localStorage.setItem('installPromptShown', 'true');
    });
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember that we've shown the prompt
    localStorage.setItem('installPromptShown', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('install.title', 'Install App')}</CardTitle>
            <Button variant="ghost" size="icon" onClick={handleDismiss} className="h-8 w-8">
              <FaTimes className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>
            {t('install.description', 'Install Fulbito Stats for a better experience')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-2">
          <p className="text-sm text-muted-foreground">
            {t('install.benefits', 'Get offline access, faster loading, and a full-screen experience')}
          </p>
        </CardContent>
        <CardFooter>
          <Button onClick={handleInstallClick} className="w-full">
            <FaDownload className="mr-2 h-4 w-4" />
            {t('install.button', 'Install Now')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
